/**
 * Браузерная реализация FxIdSdk.
 * Расширяет базовый SDK, добавляя браузер-специфичную функциональность.
 */
import { fetchProfileData, FxIdDomainPushNotificationsTokensPushNotificationPlatform, FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@fxid/openapi-client";
import { FxIdSdkCore, log } from "@fxid/sdk-core";
import { errorToString, getAuthToken, setAuthToken } from "@fxid/shared";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { createAdapter } from "./adapters";
import { BrowserEnvironment } from "./BrowserEnvironment";
import { SupportedPlatformToIframeTypeMap } from "./constants";
import { waitForAghanimPayment } from "./helpers/aghanimPaymentHelper";
import { EventAdsFailed, EventAdsFinished, EventAdsInterstitialAvailable, EventAdsSkipped, EventAdsVideoAvailable, EventChangeLanguage, EventConsentManagerStatus, EventCreateSupportRequest, EventFocusChanged, EventOnChangeOrientation, EventOnChangeUserInfo, EventOnPause, EventOnResume, MessageFxIdInitializePort, MessageFxIdPort, MethodAccountAcceptDocument, MethodAddToFavorites, MethodAdsIsInterstitialReady, 
// Реклама
MethodAdsIsVideoReady, MethodAdsShowInterstitial, MethodAdsShowVideo, MethodAdsStickyBannerHide, MethodAdsStickyBannerShow, 
// Аутентификация и пользователь
MethodAuthenticateUser, 
// Покупки и магазин
MethodBuyProduct, MethodCanReview, MethodChangeLanguage, MethodConsentManagerStatus, MethodCreateSupportRequest, MethodDefaultStoreFront, MethodGameStarted, MethodGameStopped, 
// Документы
MethodGetAccountAcceptedDocuments, 
// Социальные функции
MethodGetFriends, MethodGetGameDocuments, 
// Базовая информация
MethodGetInfo, 
// URL и навигация
MethodGetUrlHash, 
// Интеграции платформ
MethodGoogleTagManagerPushToDataLayer, MethodInviteFriends, MethodJoinCommunity, 
// Жизненный цикл игры
MethodLoadingReady, 
// Модальные окна и UI
MethodOpenModal, MethodPushRegister, MethodRequestReview, MethodReviewInfo, MethodSendCrazyGamesSdk, MethodSendFxEvent, MethodSendVkBridge, MethodSetStatus, MethodSetUrlHash, MethodShowLinkShareModal, MethodSocialSettings, MethodStatEvent, 
// Аналитика
MethodStatInitialize, MethodWordsFilter } from "./types";
import { deferred, delayPromise } from "./types/FxIdSdkUtils";
/**
 * Браузерная версия FxIdSdk
 * Использует браузерный PushSubscription для типобезопасности
 */
export class FxIdSdk extends FxIdSdkCore {
    /**
     * Получить MessageChannel для iframe коммуникации
     */
    get MessageChannel() {
        return this._messageChannel;
    }
    /**
     * Конструктор FxIdSdk
     * @param openApiClient - OpenAPI клиент для API вызовов
     * @param game - Название игры
     * @param adapter - Платформа (FxId, YandexGames, VK и т.д.)
     * @param portalConfig - Конфигурация портала
     * @param gameConfig - Конфигурация игры
     * @param os - Информация об операционной системе из useOS хука
     * @param isMobile - Флаг мобильного устройства
     * @param uiCallbacks - Опциональные UI callback'и для модальных окон
     */
    constructor(openApiClient, game, adapter, portalConfig, gameConfig, os, isMobile, dependencies) {
        const config = {
            game,
            adapter,
            portalConfig,
            gameConfig
        };
        const environment = new BrowserEnvironment();
        super(openApiClient, config, environment, dependencies);
        this._onIframePortReceived = new SimpleEventDispatcher();
        this.portTransferred = false;
        // Event dispatchers для обратной совместимости
        this._onChangeAccount = new SimpleEventDispatcher();
        this._onChangeLanguage = new SimpleEventDispatcher();
        this._onCreateSupportRequest = new SimpleEventDispatcher();
        this._onPushToken = new SimpleEventDispatcher();
        this._onAdsFinished = new SimpleEventDispatcher();
        this._onAdsFailed = new SimpleEventDispatcher();
        this._onAdsSkipped = new SimpleEventDispatcher();
        this._onAdsVideoAvailable = new SimpleEventDispatcher();
        this._onAdsInterstitialAvailable = new SimpleEventDispatcher();
        this._onDisableKeyboardInputCapture = new SimpleEventDispatcher();
        this._onEnableKeyboardInputCapture = new SimpleEventDispatcher();
        this.authenticationApiInProgress = false;
        this._adapterStatInitialized = false;
        this.webConfig = config;
        this.os = os;
        this.isMobile = isMobile;
        this.dependencies = dependencies;
        // Привязываем обработчик один раз и сохраняем его
        this.boundOnWindowPostMessage = this.onWindowPostMessage.bind(this);
        // Инициализация MessageChannel для iframe коммуникации
        this._messageChannel = new MessageChannel();
        this._messageChannel.port1.onmessage = this.subscribeToChannel.bind(this);
        // Инициализация обработчиков сообщений от iframe
        this._messageHandlers = this.createMessageHandlers();
        // Подписываемся на изменения ориентации экрана
        this.subscribeToScreenOrientationChange();
        log.info("[SDK] Initialized for browser environment");
    }
    /**
     * Инициализация SDK с браузер-специфичными настройками
     */
    async initialize() {
        await super.initialize();
        log.info("[SDK Web] Browser-specific initialization completed");
    }
    /**
     * Проверить, идет ли процесс аутентификации через API
     */
    IsAuthenticationApiInProgress() {
        return this.authenticationApiInProgress;
    }
    /**
     * Создаёт Map обработчиков для сообщений от iframe
     */
    createMessageHandlers() {
        return new Map([
            // ===== Базовая информация =====
            [MethodGetInfo, async (_message) => await this.GetInfo()],
            [MethodSocialSettings, async (_message) => await this.SocialSettings()],
            [MethodCanReview, async (_message) => await this.CanReview()],
            [MethodRequestReview, async (_message) => await this.RequestReview()],
            [MethodReviewInfo, async (_message) => await this.ReviewInfo()],
            // ===== Покупки и магазин =====
            [
                MethodBuyProduct,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.BuyProduct(requestJson);
                }
            ],
            [MethodDefaultStoreFront, async (_message) => await this.DefaultStoreFront()],
            // ===== Модальные окна и UI =====
            [
                MethodOpenModal,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.OpenModal(requestJson);
                }
            ],
            [
                MethodShowLinkShareModal,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.ShowLinkShareModal(requestJson);
                }
            ],
            // ===== Аутентификация и пользователь =====
            [
                MethodAuthenticateUser,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AuthenticateUser(requestJson);
                }
            ],
            [MethodPushRegister, async (_message) => await this.PushRegister()],
            [
                MethodChangeLanguage,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.ChangeLanguage(requestJson);
                }
            ],
            [
                MethodSetStatus,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.SetStatus(requestJson);
                }
            ],
            [
                MethodCreateSupportRequest,
                async (_message) => {
                    return await this.CreateSupportRequest();
                }
            ],
            // ===== URL и навигация =====
            [MethodGetUrlHash, async (_message) => await this.GetUrlHash()],
            [
                MethodSetUrlHash,
                async (message) => {
                    const [requestJson] = message.params;
                    await this.SetUrlHash(requestJson);
                    return await this.GetUrlHash();
                }
            ],
            // ===== Реклама =====
            [
                MethodAdsIsVideoReady,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AdsIsVideoReady(requestJson);
                }
            ],
            [
                MethodAdsShowVideo,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AdsShowVideo(requestJson);
                }
            ],
            [
                MethodAdsIsInterstitialReady,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AdsIsInterstitialReady(requestJson);
                }
            ],
            [
                MethodAdsShowInterstitial,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AdsShowInterstitial(requestJson);
                }
            ],
            [MethodAdsStickyBannerShow, async (_message) => await this.AdsStickyBannerShow()],
            [MethodAdsStickyBannerHide, async (_message) => await this.AdsStickyBannerHide()],
            // ===== Аналитика =====
            [
                MethodStatInitialize,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.StatInitialize(requestJson);
                }
            ],
            [
                MethodStatEvent,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.StatEvent(requestJson);
                }
            ],
            [
                MethodSendFxEvent,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.SendFxEvent(requestJson);
                }
            ],
            // ===== Социальные функции =====
            [
                MethodGetFriends,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.GetFriends(JSON.parse(requestJson));
                }
            ],
            [
                MethodInviteFriends,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.InviteFriends(JSON.parse(requestJson));
                }
            ],
            [
                MethodJoinCommunity,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.JoinCommunity(requestJson);
                }
            ],
            [
                MethodAddToFavorites,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AddToFavorites(requestJson);
                }
            ],
            // ===== Интеграции платформ =====
            [
                MethodGoogleTagManagerPushToDataLayer,
                async (message) => {
                    log.info("Received gtag message: %o", message);
                    const msg = message.params[0];
                    await this.GoogleTagManagerPushToDataLayer(JSON.parse(msg));
                    return Promise.resolve();
                }
            ],
            [
                MethodSendVkBridge,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.SendVkBridge(requestJson);
                }
            ],
            [
                MethodSendCrazyGamesSdk,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.SendCrazyGamesSdk(requestJson);
                }
            ],
            [
                MethodWordsFilter,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.WordsFilter(requestJson);
                }
            ],
            [
                MethodConsentManagerStatus,
                async (_message) => {
                    return await this.ConsentManagerStatus();
                }
            ],
            // ===== Жизненный цикл игры =====
            [MethodLoadingReady, async (_message) => await this.LoadingReady()],
            [MethodGameStarted, async (_message) => await this.GameStarted()],
            [MethodGameStopped, async (_message) => await this.GameStopped()],
            // ===== Документы =====
            [
                MethodGetAccountAcceptedDocuments,
                async (_message) => await this.GetAccountAcceptedDocuments()
            ],
            [
                MethodAccountAcceptDocument,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.AccountAcceptDocument(requestJson);
                }
            ],
            [
                MethodGetGameDocuments,
                async (message) => {
                    const [requestJson] = message.params;
                    return await this.GetGameDocuments(requestJson);
                }
            ]
        ]);
    }
    /**
     * Обработчик входящих сообщений от iframe через MessageChannel
     */
    async subscribeToChannel(eventMessage) {
        log.info("[MSG CHANNEL] Received message from channel: %o", eventMessage);
        const ev = eventMessage.data;
        try {
            const handler = this._messageHandlers.get(ev.method);
            if (!handler) {
                throw new Error(`[MSG CHANNEL] Unknown or not implemented method: ${ev.method}`);
            }
            const result = await handler(ev);
            log.info(`[MSG CHANNEL] Result for %s: %o`, ev.method, result);
            this._messageChannel.port1.postMessage({ ...ev, result });
        }
        catch (error) {
            log.error(`[MSG CHANNEL] Error while handling method %s with params %o. Error: %o`, ev.method, ev.params, error);
            this._messageChannel.port1.postMessage({ ...ev, error: errorToString(error) });
        }
    }
    /**
     * Получить базовую информацию о SDK и пользователе
     */
    async GetInfo() {
        if (this.socialAdapter == null) {
            throw new Error("Social adapter not initialized");
        }
        log.info("[SDK] GetInfo called");
        // Получаем профиль пользователя
        const profile = await fetchProfileData(this.config.game);
        // Находим подключенную игру
        const connectedGame = profile.ConnectedGames.find((el) => el.Game.toLowerCase() === this.config.game.toLowerCase());
        if (connectedGame == null) {
            throw new Error(`Game ${this.config.game} is not connected to FxId`);
        }
        // Получаем информацию о социальной сети
        const socialInfo = await this.socialAdapter.GetSocialInfo();
        // Получаем safe area margins
        const safeAreaMargins = this.getSafeAreaMargins();
        // Формируем результат
        const result = {
            game: this.config.game,
            sdk: {
                version: {
                    major: FxIdSdk.SdkVersionMajor,
                    minor: FxIdSdk.SdkVersionMinor
                },
                buildNumber: import.meta?.env?.VITE_APP_BUILD_ID || -1
            },
            environment: {
                os: this.os.os,
                isMobile: this.isMobile,
                safeArea: safeAreaMargins,
                supportedLocales: this.config.gameConfig.SupportedLanguages,
                defaultLocale: this.config.gameConfig.DefaultLanguage
            },
            fxId: {
                userId: profile.AccountId.toString(),
                marketingEnabled: profile.MarketingEnabled,
                locale: profile.Locale,
                geoCountry: profile.GeoCountry,
                token: getAuthToken() || "",
                gameToken: connectedGame.GameJwt,
                gameUid: connectedGame.GameUid,
                guest: profile.Guest,
                hasEmail: profile.HasEmail,
                emailValidated: profile.EmailValidated,
                hasCompletedOrder: profile.HasCompletedOrder
            },
            social: socialInfo
        };
        if (import.meta?.env?.VITE_APP_ENV_OVERRIDE_NAME !== "prod") {
            log.info("[SDK] GetInfo result: %o", result);
        }
        return result;
    }
    /**
     * Подготовка для работы в iframe
     * Начинает слушать сообщение инициализации порта от родительского окна
     */
    prepareForIframe() {
        log.info("[SDK Web] Starting to listen for %s message", MessageFxIdInitializePort);
        // Используем сохраненный обработчик
        window.addEventListener("message", this.boundOnWindowPostMessage);
    }
    /**
     * Обработчик сообщений от родительского окна для инициализации MessageChannel
     */
    onWindowPostMessage(eventMessage) {
        if (eventMessage.data === MessageFxIdInitializePort) {
            log.info("[SDK Web] Received %s message. Sending port to iframe window", MessageFxIdInitializePort);
            if (this.portTransferred) {
                log.warn("[SDK Web] Port already transferred, listener should have been removed. Removing it now.");
                // Если по какой-то причине мы сюда попали, просто удаляем слушатель
                window.removeEventListener("message", this.boundOnWindowPostMessage);
                return;
            }
            if (this._iframeWindow == null) {
                // В этом случае мы не можем удалить слушатель, так как он может понадобиться позже,
                // когда iframe будет зарегистрирован. Но это состояние ошибки, которое нужно логировать.
                log.error(`Received ${MessageFxIdInitializePort} but iframeWindow not initialized!`);
                return; // Выходим, не удаляя слушатель
            }
            this._iframeWindow.postMessage(MessageFxIdPort, "*", [this._messageChannel.port2]);
            this.portTransferred = true;
            this._onIframePortReceived.dispatchAsync(undefined);
            // Удаляем слушатель после успешной передачи порта, так как он больше не нужен.
            // Мы используем ту же самую ссылку на функцию, что и при добавлении.
            log.info("[SDK Web] Port transferred. Removing postMessage listener.");
            window.removeEventListener("message", this.boundOnWindowPostMessage);
        }
    }
    /**
     * Инициализация социального адаптера для платформы
     */
    async InitializeSocialAdapter() {
        log.info(`[SDK Web] InitializeSocialAdapter for ${this.config.adapter}`);
        if (this.socialAdapter != null) {
            log.error(`[SDK WEB] social adapter for ${this.config.adapter} already initialized`);
        }
        // Адаптеры ожидают exportedSdk - это this (FxIdSdk instance)
        this.socialAdapter = await createAdapter(this.config.adapter, this, // exportedSdk
        this.config.game, this.config.gameConfig, this.os, this.isMobile, this.dependencies);
        await this.socialAdapter.Initialize();
        // Инициализируем FxEvents, если конфиг присутствует
        if (this.config.gameConfig?.PublicWebClientConfig?.FxEvents != null) {
            const { FxIdEventProcessor } = await import("./FxIdEvents/FxIdEventProcessor");
            this.fxEventsProcessor = new FxIdEventProcessor(this, this.config.gameConfig.PublicWebClientConfig.FxEvents);
        }
        // Начинаем слушать CMP события
        this.startListenToCMPEvents();
        // Возвращаем настройки социальной сети из адаптера
        return this.socialAdapter.defaultSocialSettings;
    }
    /**
     * Получить настройки социальной сети
     */
    async SocialSettings() {
        return await this.socialAdapter.SocialSettings();
    }
    /**
     * Получить список друзей
     */
    async GetFriends(request) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized. Call InitializeSocialAdapter first.");
        }
        // Получаем друзей из социальной сети
        const socialFriends = await this.socialAdapter.GetFriends(request);
        // Получаем дополнительную информацию о друзьях из FxId API
        const friends = socialFriends.friends || [];
        const fxFriends = await this.openApiClient.Friends.fxIdWebFeaturesFriendsGetFriendsEndpoint({
            FriendsIds: friends.map((f) => f.uid)
        });
        return {
            socialFriends: socialFriends.friends,
            Friends: fxFriends.Friends
        };
    }
    /**
     * Пригласить друзей
     */
    async InviteFriends(request) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized. Call InitializeSocialAdapter first.");
        }
        await this.exitFullscreen();
        return await this.socialAdapter.InviteFriends(request);
    }
    /**
     * Отключить захват ввода с клавиатуры (для backward compatibility)
     * @deprecated Метод оставлен для совместимости со старым SDK
     */
    DispatchDisableKeyboardInputCapture() {
        log.warn("[SDK] DispatchDisableKeyboardInputCapture is deprecated and does nothing in new SDK");
        // В новой архитектуре это должно обрабатываться через core или адаптеры
    }
    /**
     * Включить захват ввода с клавиатуры (для backward compatibility)
     * @deprecated Метод оставлен для совместимости со старым SDK
     */
    DispatchEnableKeyboardInputCapture() {
        log.warn("[SDK] DispatchEnableKeyboardInputCapture is deprecated and does nothing in new SDK");
        // В новой архитектуре это должно обрабатываться через core или адаптеры
    }
    /**
     * Очистка ресурсов при уничтожении SDK
     */
    dispose() {
        log.info("[SDK Web] Disposing SDK");
        // Добавим очистку слушателя и здесь, на всякий случай
        window.removeEventListener("message", this.boundOnWindowPostMessage);
    }
    /**
     * Получить safe area insets с учетом браузерных особенностей
     */
    async getSafeAreaInsets() {
        const insets = await super.getSafeAreaInsets();
        // В браузере можем также учитывать env() переменные для iPhone X и подобных
        const envTop = this.environment.getCSSVariable("env(safe-area-inset-top)");
        const envBottom = this.environment.getCSSVariable("env(safe-area-inset-bottom)");
        const envLeft = this.environment.getCSSVariable("env(safe-area-inset-left)");
        const envRight = this.environment.getCSSVariable("env(safe-area-inset-right)");
        // Парсим и используем максимальные значения
        if (envTop) {
            const envValue = this.parseBrowserCSSValue(envTop);
            insets.top = Math.max(insets.top, envValue);
        }
        if (envBottom) {
            const envValue = this.parseBrowserCSSValue(envBottom);
            insets.bottom = Math.max(insets.bottom, envValue);
        }
        if (envLeft) {
            const envValue = this.parseBrowserCSSValue(envLeft);
            insets.left = Math.max(insets.left, envValue);
        }
        if (envRight) {
            const envValue = this.parseBrowserCSSValue(envRight);
            insets.right = Math.max(insets.right, envValue);
        }
        return insets;
    }
    /**
     * Парсить CSS значение в число
     */
    parseBrowserCSSValue(value) {
        if (!value)
            return 0;
        const match = value.match(/(\d+(?:\.\d+)?)/);
        return match ? parseFloat(match[1]) : 0;
    }
    /**
     * Выйти из полноэкранного режима
     * https://stackoverflow.com/a/58362609
     */
    async exitFullscreen() {
        try {
            // As correctly mentioned in the answered answer, exitFullscreen only works on document
            const cancellFullScreen = document.exitFullscreen ||
                document.mozCancelFullScreen ||
                document.webkitExitFullscreen ||
                document.msExitFullscreen;
            log.info("[SDK] Exiting fullscreen");
            await cancellFullScreen.call(document);
            log.info("[SDK] Exited fullscreen");
        }
        catch (err) {
            // Тут будет ошибка дескать "мы не вышли из фулскрина потому-что мы не там - нам не важно
            // ignore
        }
    }
    /**
     * Ожидание завершения оплаты через Aghanim
     * @param publicOrderId - Публичный ID заказа
     * @param options - Опции ожидания (интервал и таймаут)
     * @returns Статус оплаты
     * @private
     */
    async waitForAghanimPayment(publicOrderId, options) {
        return await waitForAghanimPayment(this.openApiClient.Store, publicOrderId, options);
    }
    // ==================== МЕТОДЫ ДЛЯ ОБРАТНОЙ СОВМЕСТИМОСТИ ====================
    // Эти методы вызываются из iframe через MessageChannel
    /**
     * Проверить возможность оставить отзыв
     */
    async CanReview() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.CanReview();
    }
    /**
     * Запросить отзыв пользователя
     */
    async RequestReview() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.RequestReview();
    }
    /**
     * Получить информацию о рейтинге
     */
    async ReviewInfo() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.ReviewInfo();
    }
    /**
     * Купить продукт
     */
    async BuyProduct(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        log.info("[SDK] Buying product %o", requestJson);
        const request = JSON.parse(requestJson);
        // Отправляем событие аналитики о начале покупки
        if (this.uiCallbacks.sendAnalyticsEvent) {
            await this.uiCallbacks.sendAnalyticsEvent("buyProductRequested", {
                game: this.config.game,
                adapter: this.config.adapter,
                product_sku: String(request.sku)
            });
        }
        // Выходим из fullscreen перед покупкой
        await this.exitFullscreen();
        // Выполняем покупку через адаптер
        const result = await this.socialAdapter.BuyProduct(request);
        // Отправляем статистику покупки
        await this.sendBuyProductStats(request, result);
        return result;
    }
    /**
     * Отправить статистику покупки
     */
    async sendBuyProductStats(request, result) {
        if (!this.uiCallbacks.sendAnalyticsEvent) {
            return;
        }
        if (result.error != null) {
            // Покупка завершилась ошибкой
            await this.uiCallbacks.sendAnalyticsEvent("buyProductError", {
                game: this.config.game,
                adapter: this.config.adapter,
                product_sku: String(request.sku),
                error: result.error
            });
        }
        else if (result.transactionId != null && result.stats != null) {
            // Покупка успешна
            await this.uiCallbacks.sendAnalyticsEvent("buyProductCompleted", {
                game: this.config.game,
                adapter: this.config.adapter,
                product_sku: String(request.sku),
                currency: result.stats.currency,
                currency_amount: result.stats.currencyAmount,
                transaction_id: result.transactionId,
                usd_amount: result.stats.usdAmount
            });
        }
    }
    /**
     * Получить данные магазина по умолчанию
     */
    async DefaultStoreFront() {
        log.info("[SDK] Returning default store front");
        const currency = await this.socialAdapter?.StoreCurrency();
        const storeAdditionalData = await this.socialAdapter?.StoreAdditionalData();
        return this.openApiClient.Store.fxIdWebFeaturesStoreStoreDataEndpoint({
            Game: this.config.game,
            WebPublishingPlatform: this.config.adapter,
            Currency: currency,
            Locale: this.environment.getLanguage(),
            StoreAdditionalData: storeAdditionalData
        });
    }
    /**
     * Открыть модальное окно
     */
    async OpenModal(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.OpenModal(requestJson);
    }
    /**
     * Показать модальное окно для шаринга ссылки
     */
    async ShowLinkShareModal(requestJson) {
        const request = JSON.parse(requestJson);
        await this.exitFullscreen();
        this.uiCallbacks.openLinkShareModal({ link: request.Link });
    }
    /**
     * Аутентифицировать пользователя
     */
    async AuthenticateUser(requestJson) {
        const request = JSON.parse(requestJson);
        // Если не FxId платформа, используем socialAdapter
        if (this.config.adapter !== FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId) {
            log.info("[SDK] Will authenticate user using this adapter", this.config.adapter);
            if (this.socialAdapter) {
                await this.socialAdapter.AuthenticateUser({});
            }
            return;
        }
        log.info("[SDK] Authenticating user", request);
        // Если есть данные импорта
        if (request.import !== undefined) {
            const SocialIdentifierType = request.import.socialIdentifierType;
            if (SocialIdentifierType === undefined) {
                throw new Error("[AUTH IMPORT] Incorrect social identifier type");
            }
            if (!this.config.gameConfig) {
                throw new Error("[AUTH IMPORT] No game gameConfig present");
            }
            try {
                const authResponse = await this.openApiClient.Auth.fxIdWebFeaturesAuthAuthenticateFromImportEndpoint({
                    SocialIdentifier: request.import.socialIdentifier,
                    SocialIdentifierType,
                    GameSystemName: this.config.gameConfig.GameSystemName
                });
                setAuthToken(authResponse.Token);
                this.environment.reload();
                return;
            }
            catch (error) {
                log.error("[AUTH IMPORT] Error while authenticating user", error);
                return;
            }
        }
        // В остальных случаях открываем модальное окно логина
        await this.exitFullscreen();
        this.uiCallbacks.openLoginModal({
            onAuthenticated: () => this.environment.reload()
        });
    }
    /**
     * Зарегистрировать push-токен
     */
    async PushRegister() {
        log.info("[SDK] Received push request from game");
        // Проверяем, находимся ли мы в iframe
        const isIframe = window.self !== window.top;
        if (isIframe || Notification.permission === "denied") {
            log.info("[SDK] [PUSH] Can't proceed with registration (iframe or denied)");
            return {};
        }
        // Получаем VAPID ключ
        const vapidPubKey = this.config.gameConfig.PublicWebClientConfig?.WebPush?.VapidPublicKey ||
            this.config.portalConfig.PortalPublicWebConfig?.WebPush?.VapidPublicKey;
        if (vapidPubKey == null) {
            log.debug("[SDK] [PUSH] No vapid keys");
            return {};
        }
        // Проверяем существующую подписку
        const { success, subscription } = await this.getPushSubscription();
        if (success && subscription) {
            return await this.registerToken(subscription);
        }
        // Если уже есть разрешение, подписываемся
        if (Notification.permission === "granted") {
            const newSubscription = await this.subscribeToPushes(vapidPubKey);
            if (newSubscription) {
                return await this.registerToken(newSubscription);
            }
            return {};
        }
        // Иначе показываем модальное окно для запроса разрешения
        await this.exitFullscreen();
        return new Promise((resolve) => {
            this.uiCallbacks.openPushPermissionModal({
                serverKey: vapidPubKey,
                onPushSubscriptionFail: () => resolve({}),
                onPushSubscriptionSuccess: async (subscription) => {
                    const result = await this.registerToken(subscription);
                    resolve(result);
                }
            });
        });
    }
    /**
     * Изменить язык
     */
    async ChangeLanguage(requestJson) {
        log.info("[SDK] Changing language to %s", requestJson);
        const langData = JSON.parse(requestJson);
        const lang = langData.lang;
        // Изменяем язык через environment (если поддерживается)
        if (typeof window.i18next !== "undefined") {
            const i18n = window.i18next;
            try {
                await i18n.changeLanguage(lang);
                // Отправляем аналитику смены языка
                if (this.uiCallbacks.sendAnalyticsEvent) {
                    await this.uiCallbacks.sendAnalyticsEvent("changeLanguage", {
                        game: this.config.game,
                        adapter: this.config.adapter,
                        new_lang: lang,
                        old_lang: i18n.resolvedLanguage || this.config.gameConfig.DefaultLanguage
                    });
                }
                // Диспатчим событие смены языка
                const args = { language: lang };
                this._onChangeLanguage.dispatch(args);
                this.iframeMessageEventChangeLanguage(lang, args);
            }
            catch (e) {
                log.error("[SDK] Error changing language:", e);
                log.warn("[SDK] Can't change language to %s. Supported languages: %o", lang, i18n.languages);
                return;
            }
        }
        else {
            log.warn("[SDK] i18next not available, language change not possible");
        }
    }
    /**
     * Установить статус пользователя
     */
    async SetStatus(requestJson) {
        const request = JSON.parse(requestJson);
        await this.openApiClient.Friends.fxIdWebFeaturesFriendsSetStatusEndpoint({
            Name: request.name,
            Priority: request.priority,
            Status: request.status
        });
        return { success: true };
    }
    /**
     * Создать запрос в поддержку
     */
    async CreateSupportRequest() {
        log.info("[SDK] Received support request from game");
        // Создаем deferred promise для получения данных от игры
        const { promise, resolve, reject } = deferred();
        // Диспатчим событие и передаем resolve в качестве callback
        this._onCreateSupportRequest.dispatch(resolve);
        // Отправляем событие в iframe для получения данных
        this.iframeMessageEventCreateSupportRequest();
        // Ожидаем ответа от игры
        const eventResult = await promise;
        log.info("[SDK] Received result from support event: %o", eventResult);
        // Выходим из fullscreen
        await this.exitFullscreen();
        // Открываем модальное окно с данными от игры
        this.uiCallbacks.openReportIssueModal({
            gameData: eventResult
        });
        // Отправляем аналитику
        if (this.uiCallbacks.sendAnalyticsEvent) {
            await this.uiCallbacks.sendAnalyticsEvent("reportIssue", {
                game: this.config.game,
                adapter: this.config.adapter
            });
        }
    }
    /**
     * Получить хэш URL
     */
    async GetUrlHash() {
        return await this.getUrlHash();
    }
    /**
     * Установить хэш URL
     */
    async SetUrlHash(requestJson) {
        const request = JSON.parse(requestJson);
        return await this.setUrlHash(request.hash);
    }
    /**
     * Проверить готовность видеорекламы
     */
    async AdsIsVideoReady(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const result = await this.socialAdapter.AdsIsVideoReady();
        return { success: result };
    }
    /**
     * Показать видеорекламу
     */
    async AdsShowVideo(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        await this.exitFullscreen();
        await this.socialAdapter.AdsShowVideo();
        return { success: true };
    }
    /**
     * Проверить готовность межстраничной рекламы
     */
    async AdsIsInterstitialReady(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const result = await this.socialAdapter.AdsIsInterstitialReady();
        return { success: result };
    }
    /**
     * Показать межстраничную рекламу
     */
    async AdsShowInterstitial(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        await this.exitFullscreen();
        await this.socialAdapter.AdsShowInterstitial();
        return { success: true };
    }
    /**
     * Показать липкий баннер
     */
    async AdsStickyBannerShow() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        await this.exitFullscreen();
        return await this.socialAdapter.AdsStickyBannerShow();
    }
    /**
     * Скрыть липкий баннер
     */
    async AdsStickyBannerHide() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.AdsStickyBannerHide();
    }
    /**
     * Инициализировать аналитику
     */
    async StatInitialize(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        log.info("[SDK] StatInitialize %o", requestJson);
        const request = JSON.parse(requestJson);
        // Отправляем событие gameStart в аналитику
        if (this.uiCallbacks.sendAnalyticsEvent) {
            await this.uiCallbacks.sendAnalyticsEvent("gameStart", {
                game: this.config.game,
                adapter: this.config.adapter
            });
        }
        // Инициализируем аналитику адаптера
        await this.socialAdapter.StatInitialize(request);
        // Устанавливаем флаг что адаптер инициализирован
        this._adapterStatInitialized = true;
        return { success: true };
    }
    /**
     * Отправить событие аналитики
     */
    async StatEvent(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const request = JSON.parse(requestJson);
        // Ждем инициализации адаптера, если еще не инициализирован
        if (!this._adapterStatInitialized) {
            log.info("[SDK] Stat is not initialized. Waiting for initialization...");
            while (!this._adapterStatInitialized) {
                await delayPromise(1000);
            }
            log.info("[SDK] Stat initialized. Sending event");
        }
        await this.socialAdapter.StatEvent(request);
        return { success: true };
    }
    /**
     * Отправить FxEvent
     */
    async SendFxEvent(requestJson) {
        if (this.fxEventsProcessor == null) {
            log.warn("[SDK] FxEventProcessor not initialized");
            return {};
        }
        try {
            const event = JSON.parse(requestJson);
            // Если есть eventCode, это in-game event для cross-promo
            if (event.eventCode) {
                // TODO: Интеграция с cross-promo service
                log.info("[SDK] Cross-promo event:", event);
            }
            else {
                // Обычное FxEvent
                await this.fxEventsProcessor.processEvent(event);
            }
        }
        catch (ex) {
            log.error("[SDK] Failed to parse JSON or process event:", ex);
        }
        return {};
    }
    /**
     * Присоединиться к сообществу
     */
    async JoinCommunity(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const request = JSON.parse(requestJson);
        await this.exitFullscreen();
        return await this.socialAdapter.JoinCommunity(request);
    }
    /**
     * Добавить в избранное
     */
    async AddToFavorites(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const request = JSON.parse(requestJson);
        await this.exitFullscreen();
        return await this.socialAdapter.AddToFavorites(request);
    }
    /**
     * Отправить данные в Google Tag Manager
     */
    async GoogleTagManagerPushToDataLayer(obj) {
        try {
            const emailKey = Object.keys(obj).find((key) => key.toLowerCase() === "email");
            const email = emailKey ? obj[emailKey] : null;
            if (email) {
                return window.dataLayer?.push(obj);
            }
            else {
                // Получаем профиль и добавляем email
                const profile = await fetchProfileData(this.config.game);
                const newObj = {
                    ...obj,
                    email: profile.Email
                };
                return window.dataLayer?.push(newObj);
            }
        }
        catch (err) {
            log.error("[SDK] Error while trying to send to gtag", err);
        }
    }
    /**
     * Отправить сообщение в VK Bridge
     */
    async SendVkBridge(requestJson) {
        // Вызываем метод адаптера (если он есть)
        if (this.socialAdapter && this.socialAdapter.SendVkBridge) {
            const request = JSON.parse(requestJson);
            return await this.socialAdapter.SendVkBridge(request.method, request.props);
        }
        throw new Error("VK adapter not initialized or does not support SendVkBridge");
    }
    /**
     * Отправить сообщение в CrazyGames SDK
     */
    async SendCrazyGamesSdk(requestJson) {
        // Вызываем метод адаптера (если он есть)
        if (this.socialAdapter && this.socialAdapter.SendCrazyGamesSdk) {
            const request = JSON.parse(requestJson);
            return await this.socialAdapter.SendCrazyGamesSdk(request.method, request.props);
        }
        throw new Error("CrazyGames adapter not initialized or does not support SendCrazyGamesSdk");
    }
    /**
     * Фильтр слов (цензура)
     */
    async WordsFilter(requestJson) {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        const request = JSON.parse(requestJson);
        return await this.socialAdapter.WordsFilter(request);
    }
    /**
     * Получить статус Consent Manager
     */
    async ConsentManagerStatus() {
        // https://help.consentmanager.net/books/cmp/page/javascript-api
        if (window.__cmp != null) {
            log.info("[CMP] Checking CMP status");
            const x = window.__cmp("consentStatus", null, null, false);
            log.info("[CMP] CMP status: %o", x);
            if (typeof x === "object" && "consentExists" in x) {
                const value = {
                    consentExists: x.consentExists,
                    consentData: x.consentData,
                    cmpData: this.getCmpData()
                };
                return Promise.resolve(value);
            }
        }
        return Promise.resolve({ consentExists: false, consentData: {}, cmpData: {} });
    }
    /**
     * Сигнал готовности загрузки
     */
    async LoadingReady() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.LoadingReady();
    }
    /**
     * Сигнал старта игры
     */
    async GameStarted() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.GameStarted();
    }
    /**
     * Сигнал остановки игры
     */
    async GameStopped() {
        if (!this.socialAdapter) {
            throw new Error("Social adapter not initialized");
        }
        return await this.socialAdapter.GameStopped();
    }
    /**
     * Получить принятые документы аккаунта
     */
    async GetAccountAcceptedDocuments() {
        return await this.getAccountAcceptedDocumentsApi();
    }
    /**
     * Принять документ
     */
    async AccountAcceptDocument(requestJson) {
        return await this.accountAcceptDocumentApi(requestJson);
    }
    /**
     * Получить игровые документы
     */
    async GetGameDocuments(requestJson) {
        return await this.getGameDocumentsApi(requestJson);
    }
    // ========== IframeMessage Event Methods ==========
    /**
     * Отправить событие паузы в iframe
     */
    iframeMessageEventOnPause() {
        const message = {
            method: EventOnPause,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие возобновления в iframe
     */
    iframeMessageEventOnResume() {
        const message = {
            method: EventOnResume,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие доступности видеорекламы в iframe
     */
    iframeMessageEventAdsVideoAvailable() {
        const message = {
            method: EventAdsVideoAvailable,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие статуса Consent Manager в iframe
     */
    iframeMessageEventConsentManagerStatus(status) {
        const message = {
            method: EventConsentManagerStatus,
            params: [],
            result: status
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие доступности межстраничной рекламы в iframe
     */
    iframeMessageEventAdsInterstitialAvailable() {
        const message = {
            method: EventAdsInterstitialAvailable,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие завершения рекламы в iframe
     */
    iframeMessageEventAdsFinished() {
        const message = {
            method: EventAdsFinished,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие ошибки рекламы в iframe
     */
    iframeMessageEventAdsFailed() {
        const message = {
            method: EventAdsFailed,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие пропуска рекламы в iframe
     */
    iframeMessageEventAdsSkipped() {
        const message = {
            method: EventAdsSkipped,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие изменения фокуса в iframe
     */
    iframeMessageEventFocusChanged() {
        const message = {
            method: EventFocusChanged,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие изменения информации о пользователе в iframe
     */
    iframeMessageEventOnChangeUserInfo(changeInfoEvent) {
        const message = {
            method: EventOnChangeUserInfo,
            params: [],
            result: changeInfoEvent
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие смены языка в iframe
     */
    iframeMessageEventChangeLanguage(lang, args) {
        const message = {
            method: EventChangeLanguage,
            params: [lang],
            result: args
        };
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие изменения ориентации в iframe
     */
    iframeMessageEventOnChangeOrientation(args) {
        const message = {
            method: EventOnChangeOrientation,
            params: [],
            result: args
        };
        log.info("[SDK] Sending orientation change event to iframe");
        this._messageChannel.port1.postMessage(message);
    }
    /**
     * Отправить событие создания запроса в поддержку в iframe
     */
    iframeMessageEventCreateSupportRequest() {
        const message = {
            method: EventCreateSupportRequest,
            params: []
        };
        this._messageChannel.port1.postMessage(message);
    }
    // ========== Public Dispatch Methods (Override Core) ==========
    /**
     * Диспатчит событие доступности видеорекламы
     * Переопределяет метод из core для добавления отправки в iframe
     */
    DispatchAdsVideoAvailable() {
        super.dispatchAdsVideoAvailable();
        this._onAdsVideoAvailable.dispatch({});
        this.iframeMessageEventAdsVideoAvailable();
    }
    /**
     * Диспатчит событие доступности межстраничной рекламы
     */
    DispatchAdsInterstitialAvailable() {
        super.dispatchAdsInterstitialAvailable();
        this._onAdsInterstitialAvailable.dispatch({});
        this.iframeMessageEventAdsInterstitialAvailable();
    }
    /**
     * Диспатчит событие завершения рекламы
     */
    DispatchAdsFinished() {
        super.dispatchAdsFinished();
        this._onAdsFinished.dispatch({});
        this.iframeMessageEventAdsFinished();
    }
    /**
     * Диспатчит событие ошибки рекламы
     */
    DispatchAdsFailed() {
        super.dispatchAdsFailed();
        this._onAdsFailed.dispatch({});
        this.iframeMessageEventAdsFailed();
    }
    /**
     * Диспатчит событие пропуска рекламы
     */
    DispatchAdsSkipped() {
        super.dispatchAdsSkipped();
        this._onAdsSkipped.dispatch({});
        this.iframeMessageEventAdsSkipped();
    }
    /**
     * Диспатчит событие изменения фокуса
     */
    DispatchFocusChanged(data) {
        super.dispatchFocusChanged(data);
        this.iframeMessageEventFocusChanged();
    }
    /**
     * Диспатчит событие паузы
     */
    DispatchPause() {
        super.dispatchPause();
        this.iframeMessageEventOnPause();
    }
    /**
     * Диспатчит событие возобновления
     */
    DispatchResume() {
        super.dispatchResume();
        this.iframeMessageEventOnResume();
    }
    /**
     * Диспатчит событие статуса Consent Manager
     */
    DispatchConsentManagerStatus(status) {
        super.dispatchConsentManagerStatus(status);
        this.iframeMessageEventConsentManagerStatus(status);
    }
    /**
     * Диспатчит событие изменения информации о пользователе
     * Переопределяет protected метод из core
     */
    dispatchOnChangedUserInfo(changeInfoEvent) {
        super.dispatchOnChangedUserInfo(changeInfoEvent);
        this.iframeMessageEventOnChangeUserInfo(changeInfoEvent);
    }
    // ========== Helper Methods ==========
    /**
     * Сохранить в буфер обмена
     */
    async saveToClipboard(request) {
        const req = JSON.parse(request);
        try {
            const permissionStatus = await navigator.permissions.query({
                name: "clipboard-write"
            });
            const available = permissionStatus.state === "granted" || permissionStatus.state === "prompt";
            if (available) {
                await navigator.clipboard.writeText(req.data);
                return { savedToClipboard: true };
            }
            else if (req.showModalOnFail === true) {
                this.uiCallbacks.openTextCopyModal({ text: req.data });
            }
            return { savedToClipboard: false };
        }
        catch (error) {
            log.warn("[SDK] Error saving to clipboard:", error);
            if (req.showModalOnFail === true) {
                this.uiCallbacks.openTextCopyModal({ text: req.data });
            }
            return { savedToClipboard: false };
        }
    }
    /**
     * Зарегистрировать push токен на сервере
     */
    async registerToken(subscription) {
        await this.openApiClient.PushToken.fxIdWebFeaturesPushTokensSavePushTokenEndpoint({
            Game: this.config.game,
            Platform: FxIdDomainPushNotificationsTokensPushNotificationPlatform.Web,
            Token: JSON.stringify(subscription)
        });
        const result = { Token: JSON.stringify(subscription) };
        // Диспатчим событие получения push токена
        this._onPushToken.dispatch(result);
        return result;
    }
    /**
     * Получить текущую push подписку
     */
    async getPushSubscription() {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            return { success: subscription !== null, subscription: subscription || undefined };
        }
        catch (error) {
            log.error("[SDK] Error getting push subscription:", error);
            return { success: false };
        }
    }
    /**
     * Подписаться на push уведомления
     */
    async subscribeToPushes(vapidPublicKey) {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
            });
            return subscription;
        }
        catch (error) {
            log.error("[SDK] Error subscribing to pushes:", error);
            return null;
        }
    }
    /**
     * Конвертировать base64 строку в Uint8Array для VAPID ключа
     */
    urlBase64ToUint8Array(base64String) {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
    /**
     * Получить Safe Area Margins из CSS переменных
     */
    getSafeAreaMargins() {
        const result = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
        };
        const topString = getComputedStyle(document.documentElement).getPropertyValue("--sat");
        const bottomString = getComputedStyle(document.documentElement).getPropertyValue("--sab");
        const leftString = getComputedStyle(document.documentElement).getPropertyValue("--sal");
        const rightString = getComputedStyle(document.documentElement).getPropertyValue("--sar");
        if (topString !== "") {
            const val = parseInt(topString);
            if (!isNaN(val)) {
                result.top = val;
            }
        }
        if (bottomString !== "") {
            const val = parseInt(bottomString);
            if (!isNaN(val)) {
                result.bottom = val;
            }
        }
        if (leftString !== "") {
            const val = parseInt(leftString);
            if (!isNaN(val)) {
                result.left = val;
            }
        }
        if (rightString !== "") {
            const val = parseInt(rightString);
            if (!isNaN(val)) {
                result.right = val;
            }
        }
        return result;
    }
    /**
     * Подписаться на изменения ориентации экрана
     */
    subscribeToScreenOrientationChange() {
        try {
            screen.orientation.addEventListener("change", (event) => {
                try {
                    if (event.target == null)
                        return;
                    const target = event.target;
                    const type = target.type;
                    const angle = target.angle;
                    log.info("[SDK] ScreenOrientation change: %s, %d degrees", type, angle);
                    log.info("[SDK] New safe area: %o", this.getSafeAreaMargins());
                    // Отправляем событие изменения ориентации в iframe
                    this.iframeMessageEventOnChangeOrientation({
                        orientationType: type,
                        orientationAngle: angle,
                        orientation: type.startsWith("portrait") ? "portrait" : "landscape",
                        safeArea: this.getSafeAreaMargins()
                    });
                }
                catch (err) {
                    log.error("[SDK] Error handling orientation change:", err);
                }
            });
        }
        catch (err) {
            log.warn("[SDK] Screen orientation API is not supported:", err);
        }
    }
    /**
     * Получить данные CMP (Consent Manager Platform)
     */
    getCmpData() {
        if (window.__cmp == null) {
            return null;
        }
        return window.__cmp("getCMPData");
    }
    /**
     * Начать прослушивание CMP (Consent Manager Platform) событий
     * https://help.consentmanager.net/books/cmp/page/cmp-events
     */
    startListenToCMPEvents() {
        if (window.__cmp != null) {
            log.info("[SDK] [CMP] Starting to listen to CMP events");
            window.__cmp("addEventListener", [
                "consent",
                (e, o) => {
                    log.info("[SDK] [CMP] Consent event. E: %o, O: %o", e, o);
                    if (window.__cmp != null) {
                        const x = window.__cmp("consentStatus", null, null, false);
                        log.info("[SDK] [CMP] CMP status: %o", x);
                        if (typeof x === "object" && "consentExists" in x) {
                            const status = {
                                consentExists: x.consentExists,
                                consentData: x.consentData,
                                cmpData: this.getCmpData()
                            };
                            this.dispatchConsentManagerStatus(status);
                        }
                    }
                },
                false
            ], null);
        }
    }
    /**
     * Логин в FxId через текущий адаптер
     * Огромный метод с логикой для всех поддерживаемых платформ
     */
    async LoginToFxId() {
        log.info("[SDK Web] Logging in to FxId");
        const request = {
            Game: this.config.game
        };
        // Определяем платформу и заполняем соответствующие данные
        switch (this.config.adapter) {
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId: {
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId];
                const searchParams = new URL(String(window.location)).searchParams;
                const LoginCode = searchParams.get("loginCode");
                if (LoginCode) {
                    log.info("Found loginCode in URL:", LoginCode);
                    log.info("Appending loginCode to request");
                    request.FxIdLogin = { LoginCode };
                }
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay: {
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay];
                request.VkontaktePlayLogin = {
                    QueryString: location.search
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom: {
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom];
                request.VkontakteComLogin = {
                    QueryString: location.search
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki: {
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki];
                request.OdnoklassnikiLogin = {
                    QueryString: location.search
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames: {
                const yandexPlayerSignature = await this.socialAdapter.GetPlayerSignature();
                const yandexSocialInfo = await this.socialAdapter.GetSocialInfo();
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames];
                request.YandexGamesLogin = {
                    GetPlayerSignature: yandexPlayerSignature.signature,
                    EnvironmentI18nLang: yandexSocialInfo.socialLocale ?? "ru"
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.AbsoluteGames: {
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.AbsoluteGames];
                request.AbsoluteGamesLogin = {
                    QueryString: location.search
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Kongregate: {
                const kgPlayerInfo = await this.socialAdapter.GetPlayer();
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Kongregate];
                request.Kongregate = {
                    UserId: kgPlayerInfo.userId,
                    UserName: kgPlayerInfo.userName,
                    Token: kgPlayerInfo.gameAuthToken,
                    IsGuest: kgPlayerInfo.isGuest
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.CrazyGames: {
                const cgToken = await this.socialAdapter.GetUserToken();
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.CrazyGames];
                request.CrazyGames = {
                    Token: cgToken ?? "",
                    IsGuest: cgToken == null
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookGames: {
                const fbGamesPlayerInfo = this.socialAdapter.GetLoginStatusResponse();
                if (fbGamesPlayerInfo == null) {
                    throw new Error("Facebook is not logged in");
                }
                if (fbGamesPlayerInfo.authResponse.signedRequest == null) {
                    throw new Error("Facebook player does not have signedRequest");
                }
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookGames];
                request.FacebookGamesLogin = {
                    SignedRequest: fbGamesPlayerInfo.authResponse.signedRequest
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames: {
                const signedFbInstantData = await window.FBInstant.player.getSignedASIDAsync();
                if (signedFbInstantData == null) {
                    throw new Error("Failed to get signedFbInstantData");
                }
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames];
                request.FacebookInstantGames = {
                    ApplicationScopedId: signedFbInstantData.getASID(),
                    Signature: signedFbInstantData.getSignature()
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ: {
                const token = await this.socialAdapter.GetUserIdToken();
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ];
                request.WizQ = {
                    Token: token
                };
                break;
            }
            case FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Telegram: {
                const initData = await this.socialAdapter.GetInitData();
                request.Social =
                    FxIdDomainStoreEnumsSupportedWebPublishingPlatform[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Telegram];
                request.Telegram = {
                    InitData: initData
                };
                break;
            }
            default:
                throw new Error(`Unknown adapter: ${this.config.adapter}`);
        }
        if (request.Social == null) {
            throw new Error(`Unknown social for adapter ${this.config.adapter}`);
        }
        log.info("Logging in");
        await this.AuthenticateApi(request);
        // Отправляем аналитику
        if (this.uiCallbacks.sendAnalyticsEvent) {
            await this.uiCallbacks.sendAnalyticsEvent("logUserInFxId", {
                game: this.config.game,
                adapter: SupportedPlatformToIframeTypeMap[this.config.adapter]
            });
        }
        // Регистрируем share handlers на адаптере
        await this.socialAdapter.RegisterShareHandlers();
        // Выполняем дополнительную инициализацию после успешного логина
        await this.socialAdapter.OnAfterLogin();
    }
    /**
     * Аутентификация через API
     * Обрабатывает смену аккаунта и диспатчит соответствующие события
     */
    async AuthenticateApi(request) {
        let changeEvent = undefined;
        let changeInfoEvent = undefined;
        let authResponse;
        try {
            this.authenticationApiInProgress = true;
            log.info("[SDK] Authenticating with request: %o. Last response was: %o", request, this._lastAuthResponse);
            const lastAuthResponseAccountId = this._lastAuthResponse?.AccountId;
            // Вызываем API аутентификации
            authResponse = await this.openApiClient.Auth.fxIdWebFeaturesAuthAuthenticateFromSocialEndpoint(request);
            log.info("[SDK] Received auth response: %o", authResponse);
            // Сохраняем токен
            setAuthToken(authResponse.Token);
            // Инвалидируем все кэши (если используется queryClient)
            // await queryClient.invalidateQueries();
            this._lastAuthResponse = authResponse;
            // Проверяем, сменился ли аккаунт
            if (lastAuthResponseAccountId != null && lastAuthResponseAccountId !== authResponse.AccountId) {
                log.info("[SDK] Account changed from %s to %s. Sending account changed event", lastAuthResponseAccountId, authResponse.AccountId);
                changeEvent = {
                    oldAccountId: lastAuthResponseAccountId,
                    newAccountId: authResponse.AccountId
                };
            }
            // Получаем новую информацию о пользователе
            const newInfo = await this.GetInfo();
            changeInfoEvent = { newInfo };
        }
        finally {
            this.authenticationApiInProgress = false;
        }
        // Диспатчим событие смены аккаунта, если произошла смена
        if (changeEvent != null) {
            log.info("[SDK] Dispatching onChangeAccount event %o", changeEvent);
            this._onChangeAccount.dispatch(changeEvent);
        }
        // Диспатчим событие изменения информации о пользователе
        if (changeInfoEvent != null) {
            this.dispatchOnChangedUserInfo(changeInfoEvent);
        }
        return {
            authResponse,
            changeEvent,
            changeInfoEvent
        };
    }
    /**
     * Получить текущий адаптер платформы
     */
    get Adapter() {
        return this.config.adapter;
    }
    /**
     * NOTE: Это хак для версии GC от 2024-05 - надо будет удалить. НЕ ИСПОЛЬЗОВАТЬ
     * @deprecated Используйте OnFocusChanged
     */
    get OnFocusChange() {
        log.warn("[SDK] OnFocusChange is deprecated, use OnFocusChanged");
        return this.OnFocusChanged;
    }
    /**
     * Проверяет, является ли устройство мобильным
     */
    IsMobile() {
        return this.isMobile;
    }
    /**
     * Регистрация iframe окна
     * Используется для установки коммуникации между SDK и iframe через MessageChannel
     */
    async RegisterIframeWindow(iframeWindow) {
        if (this._iframeWindow != null) {
            log.error("[SDK] IFrame window already initialized. Releasing old initialization and saving new");
            this._iframeWindow = undefined;
            // Т.к. предыдущий messageChannel уже ушел в старый айфрейм и использовать его больше нельзя (он transfered)
            // то надо создать новый
            this._messageChannel = new MessageChannel();
            this._messageChannel.port1.onmessage = this.subscribeToChannel.bind(this);
            this.portTransferred = false;
        }
        log.info("[SDK] Registering iframe %o", iframeWindow);
        this._iframeWindow = iframeWindow;
        // Вызываем RegisterIframeWindow на адаптере, если он есть и поддерживает это
        if (this.socialAdapter) {
            await this.socialAdapter.RegisterIframeWindow();
        }
    }
    /**
     * Событие получения iframe порта
     * Диспатчится когда MessageChannel port успешно передан в iframe
     */
    get OnIframePortReceived() {
        return this._onIframePortReceived.asEvent();
    }
    /**
     * Событие смены аккаунта
     */
    get OnChangeAccount() {
        return this._onChangeAccount.asEvent();
    }
    /**
     * Событие смены языка
     */
    get OnChangeLanguage() {
        return this._onChangeLanguage.asEvent();
    }
    /**
     * Событие создания запроса в поддержку
     */
    get OnCreateSupportRequest() {
        return this._onCreateSupportRequest.asEvent();
    }
    /**
     * Событие получения push токена
     */
    get OnPushToken() {
        return this._onPushToken.asEvent();
    }
    /**
     * Событие завершения рекламы
     */
    get OnAdsFinished() {
        return this._onAdsFinished.asEvent();
    }
    /**
     * Событие ошибки рекламы
     */
    get OnAdsFailed() {
        return this._onAdsFailed.asEvent();
    }
    /**
     * Событие пропуска рекламы
     */
    get OnAdsSkipped() {
        return this._onAdsSkipped.asEvent();
    }
    /**
     * Событие доступности видеорекламы
     */
    get OnAdsVideoAvailable() {
        return this._onAdsVideoAvailable.asEvent();
    }
    /**
     * Событие доступности межстраничной рекламы
     */
    get OnAdsInterstitialAvailable() {
        return this._onAdsInterstitialAvailable.asEvent();
    }
    /**
     * Событие отключения захвата ввода с клавиатуры
     */
    get OnDisableKeyboardInputCapture() {
        return this._onDisableKeyboardInputCapture.asEvent();
    }
    /**
     * Событие включения захвата ввода с клавиатуры
     */
    get OnEnableKeyboardInputCapture() {
        return this._onEnableKeyboardInputCapture.asEvent();
    }
}
// SDK версия
FxIdSdk.SdkVersionMajor = 1;
FxIdSdk.SdkVersionMinor = 6;
